import { useEffect, useState } from 'preact/hooks';
import { isRecentSearchVisible } from '../../utils/basePlaceType';
import { isCityToCityRecentSearch, readRecentSearches } from '../../utils/recentTrips';
import { camelizeKeys } from 'humps';
import useWidgetConfig from '../WidgetConfig';
import usePlaces from '../Places';

const useRecommendedRoutes = ({
  showRecentSearches,
  showPopularDestinations,
  hybridSearchEnabled = false,
  defaultPlaceType = null,
}) => {
  const [recentSearches, setRecentSearches] = useState({});
  const { maxRecentSearches, recentSearchesLS } = useWidgetConfig();
  const currentPlaces = usePlaces();

  const RECENT_SEARCHES = 'recentSearches';

  const hasRecentSearches = Boolean(Object.keys(recentSearches).length);

  const setUpRecentSearches = () => {
    if (!showRecentSearches || !maxRecentSearches) return;

    const recentSearchesFormatted = readRecentSearches(recentSearchesLS).map(
      recentSearch => ({
        ...recentSearch,
        origin: {
          ...recentSearch.origin,
          recentSearch: true,
        },
        destination: {
          ...recentSearch.destination,
          recentSearch: true,
        },
      })
    );

    setRecentSearches({
      type: RECENT_SEARCHES,
      routes: camelizeKeys(recentSearchesFormatted),
    });
  };

  const createRecommendedList = () => {
    const recommendedRoutes = [];

    if (hasRecentSearches) {
      // The stored list is uncut, so the cut happens here and follows the hybrid value, which
      // can change after mount. Hidden entries are never deleted: they come back with the flag.
      // With hybrid search on only one side may be a city, so a city→city search is hidden.
      // With it off and a base type, a search shows only when both sides match, and hidden
      // entries are dropped before the cut so they do not use a slot.
      let routes;
      if (hybridSearchEnabled) {
        routes = recentSearches.routes
          .slice(0, maxRecentSearches)
          .filter(route => !isCityToCityRecentSearch(route));
      } else if (defaultPlaceType) {
        routes = recentSearches.routes
          .filter(route => isRecentSearchVisible(route, defaultPlaceType))
          .slice(0, maxRecentSearches);
      } else {
        routes = recentSearches.routes.slice(0, maxRecentSearches);
      }
      if (routes.length) recommendedRoutes.push({ routes });
    }

    if (showPopularDestinations && currentPlaces?.places?.length) {
      recommendedRoutes.push({
        type: 'popularDestinations',
        routes: camelizeKeys(
          currentPlaces.places.slice(0, 3).map(place => ({
            destination: {
              ...place,
              isPopularDestination: true,
            },
          }))
        ),
      });
    }
    return recommendedRoutes;
  };

  useEffect(() => {
    setUpRecentSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: Add the recommended routes by profile too
  const recommendedRoutes = createRecommendedList();
  return {
    exists: Boolean(recommendedRoutes.length),
    recommendedRoutes,
  };
};

export default useRecommendedRoutes;
